import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gameAPI, characterAPI, adminAPI, sessionAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../contexts/SocketContext';
import DiceRoller from '../components/DiceRoller';
import Journal from '../components/Journal';
import CharacterPanel from '../components/CharacterPanel';
import Inventory from '../components/Inventory';
import GameSettings from '../components/GameSettings';
import PlayerList from '../components/PlayerList';
import TurnTimer from '../components/TurnTimer';
import { ToastContainer } from '../components/Toast';
import ProfilePopup from '../components/ProfilePopup';
import './Game.css';

export default function Game() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    isConnected,
    connect,
    joinGameRoom,
    leaveGameRoom,
    handleTyping,
    onPlayerJoined,
    onPlayerLeft,
    onPlayerKicked,
    onPlayerBanned,
    onPlayerDisconnected,
    onNewMessage,
    onTurnUpdate,
    onGameStateUpdate,
    onTurnTimerWarning,
    onTurnAutoEnded
  } = useSocket();

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [game, setGame] = useState(null);
  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  const [showJournal, setShowJournal] = useState(false);
  const [showCharacter, setShowCharacter] = useState(false);
  const [showInventory, setShowInventory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const [pendingRoll, setPendingRoll] = useState(null);
  const [showDiceRoller, setShowDiceRoller] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState([]);

  // Multiplayer state
  const [participants, setParticipants] = useState([]);
  const [turnState, setTurnState] = useState(null);
  const isHost = game?.user === user?._id || game?.hostUser === user?._id;
  const isMultiplayer = game?.isMultiplayer && participants.length > 1;

  // Toast notifications
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  // Turn-based restrictions
  const isYourTurn = !isMultiplayer || turnState?.currentUserId === user?._id;
  const canSendMessage = !isMultiplayer || (isYourTurn && (turnState?.messageCount || 0) < 3);
  const canUseConsumable = !isMultiplayer || (isYourTurn && (turnState?.consumablesUsed || 0) < 2);
  const canAttack = !isMultiplayer || (isYourTurn && !turnState?.attackUsed);

  // Get current player name for turn display
  const currentTurnPlayer = participants.find(
    p => (p.user?._id || p.user) === turnState?.currentUserId
  );

  // Chat enhancements
  const [isOOC, setIsOOC] = useState(false); // Out-of-character mode
  const MESSAGE_CHAR_LIMIT = 250;
  const charsRemaining = MESSAGE_CHAR_LIMIT - input.length;

  // Profile popup state
  const [viewingProfile, setViewingProfile] = useState(null); // userId to view

  useEffect(() => {
    loadGame();
  }, [gameId]);

  useEffect(() => {
    scrollToBottom();
  }, [game?.messages]);

  // Join game room for real-time updates
  useEffect(() => {
    if (gameId && isConnected) {
      joinGameRoom(gameId);
    }

    return () => {
      if (gameId) {
        leaveGameRoom();
      }
    };
  }, [gameId, isConnected, joinGameRoom, leaveGameRoom]);

  // Load participants for multiplayer games
  useEffect(() => {
    if (game?.isMultiplayer) {
      loadParticipants();
    }
  }, [game?.isMultiplayer, gameId]);

  // Socket event listeners
  useEffect(() => {
    if (!isConnected) return;

    const unsubPlayerJoined = onPlayerJoined((data) => {
      setParticipants(prev => {
        const exists = prev.some(p => p.user?._id === data.userId || p.user === data.userId);
        if (exists) return prev;
        return [...prev, {
          user: data.userId,
          username: data.username,
          characterName: data.characterName,
          isActive: true
        }];
      });

      // Add system message
      setGame(prev => prev ? {
        ...prev,
        messages: [...prev.messages, {
          role: 'system',
          content: `${data.characterName} (${data.username}) has joined the adventure!`,
          timestamp: new Date()
        }]
      } : prev);
    });

    const unsubPlayerLeft = onPlayerLeft((data) => {
      setParticipants(prev =>
        prev.map(p =>
          (p.user?._id === data.userId || p.user === data.userId)
            ? { ...p, isActive: false }
            : p
        )
      );

      setGame(prev => prev ? {
        ...prev,
        messages: [...prev.messages, {
          role: 'system',
          content: `${data.characterName || data.username} (${data.username}) has left the adventure.`,
          timestamp: new Date()
        }]
      } : prev);
    });

    const unsubPlayerKicked = onPlayerKicked((data) => {
      setParticipants(prev =>
        prev.map(p =>
          (p.user?._id === data.userId || p.user === data.userId)
            ? { ...p, isActive: false }
            : p
        )
      );

      // If current user was kicked, show a toast and redirect
      if (data.userId === user?._id) {
        const reason = data.reason === 'afk'
          ? 'You have been removed for being AFK.'
          : 'You have been removed from the session.';
        addToast(reason, 'error', 5000);
      }
    });

    const unsubPlayerBanned = onPlayerBanned((data) => {
      setParticipants(prev =>
        prev.map(p =>
          (p.user?._id === data.userId || p.user === data.userId)
            ? { ...p, isActive: false, isBanned: true }
            : p
        )
      );

      // If current user was banned, show a toast and redirect
      if (data.userId === user?._id) {
        addToast('You have been banned from this session.', 'error', 8000);
        setTimeout(() => navigate('/dashboard'), 3000);
      }
    });

    const unsubPlayerDisconnected = onPlayerDisconnected((data) => {
      setParticipants(prev =>
        prev.map(p =>
          (p.user?._id === data.userId || p.user === data.userId)
            ? { ...p, user: { ...p.user, isOnline: false } }
            : p
        )
      );
    });

    const unsubNewMessage = onNewMessage((data) => {
      setGame(prev => prev ? {
        ...prev,
        messages: [...prev.messages, data.message]
      } : prev);
    });

    const unsubTurnUpdate = onTurnUpdate((data) => {
      setTurnState(data.turnState);
      if (data.participants) {
        setParticipants(data.participants);
      }
    });

    const unsubGameStateUpdate = onGameStateUpdate((data) => {
      if (data.character) {
        setCharacter(data.character);
      }
      if (data.gameState) {
        setGame(prev => prev ? { ...prev, ...data.gameState } : prev);
      }
    });

    // Turn timer warning from server
    const unsubTurnWarning = onTurnTimerWarning?.((data) => {
      if (data.userId === user?._id) {
        addToast(`${data.secondsLeft} seconds remaining in your turn!`, 'warning', 8000);
      }
    });

    // Turn auto-ended from server
    const unsubTurnAutoEnded = onTurnAutoEnded?.((data) => {
      setTurnState(data.newTurnState);
      if (data.previousUserId === user?._id) {
        addToast('Your turn has ended automatically.', 'error', 5000);
      }
    });

    return () => {
      unsubPlayerJoined();
      unsubPlayerLeft();
      unsubPlayerKicked();
      unsubPlayerBanned();
      unsubPlayerDisconnected();
      unsubNewMessage();
      unsubTurnUpdate();
      unsubGameStateUpdate();
      unsubTurnWarning?.();
      unsubTurnAutoEnded?.();
    };
  }, [isConnected, onPlayerJoined, onPlayerLeft, onPlayerKicked, onPlayerBanned, onPlayerDisconnected, onNewMessage, onTurnUpdate, onGameStateUpdate, onTurnTimerWarning, onTurnAutoEnded, user?._id, navigate]);

  const loadParticipants = async () => {
    try {
      const response = await sessionAPI.getParticipants(gameId);
      setParticipants(response.data.participants || []);
      setTurnState(response.data.turnState || null);
    } catch (err) {
      console.error('Failed to load participants:', err);
    }
  };

  // Toast helper functions
  const addToast = (message, type = 'info', duration = 5000) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, message, type, duration }]);
    return id;
  };

  const dismissToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Turn timer callbacks
  const handleTimeWarning = useCallback(() => {
    addToast('30 seconds remaining in your turn!', 'warning', 8000);
  }, []);

  const handleTimeExpired = useCallback(async () => {
    addToast('Your turn has ended automatically.', 'error', 5000);

    // Auto-end turn
    try {
      await sessionAPI.endTurn(gameId);
    } catch (err) {
      console.error('Failed to auto-end turn:', err);
    }
  }, [gameId]);

  // Manually end turn
  const handleEndTurn = async () => {
    try {
      const response = await sessionAPI.endTurn(gameId);
      setTurnState(response.data.turnState);
      addToast('Turn ended.', 'info', 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to end turn');
    }
  };

  // Host can force take their turn (emergency recovery)
  const handleForceHostTurn = async () => {
    try {
      await sessionAPI.forceHostTurn(gameId);
      addToast('Turn control transferred to you.', 'success', 3000);
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to take control', 'error', 5000);
    }
  };

  // Periodic turn check for multiplayer games (checks every 30 seconds)
  useEffect(() => {
    if (!isMultiplayer || !gameId) return;

    const checkTurnTimeout = async () => {
      try {
        const response = await sessionAPI.checkTurn(gameId);
        if (response.data.turnAdvanced) {
          setTurnState(response.data.turnState);
          if (response.data.participants) {
            setParticipants(response.data.participants);
          }
        }
      } catch (err) {
        console.error('Turn check failed:', err);
      }
    };

    // Check immediately on load
    checkTurnTimeout();

    // Then check every 30 seconds
    const interval = setInterval(checkTurnTimeout, 30000);

    return () => clearInterval(interval);
  }, [isMultiplayer, gameId]);

  const loadGame = async () => {
    try {
      const response = await gameAPI.getOne(gameId);
      setGame(response.data.game);
      setCharacter(response.data.game.character);
    } catch (err) {
      setError('Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendAction = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;

    // Check turn restrictions in multiplayer
    if (isMultiplayer && !isYourTurn) {
      setError("It's not your turn!");
      return;
    }

    if (isMultiplayer && !canSendMessage) {
      setError('You have reached the maximum of 3 messages for this turn.');
      return;
    }

    const action = input.trim();
    setInput('');
    setSending(true);

    // Check if this is a slash command
    if (action.startsWith('/')) {
      await handleSlashCommand(action);
      return;
    }

    // Handle OOC (out-of-character) messages - don't process with AI
    if (isOOC) {
      const oocMessage = {
        role: 'ooc',
        content: action,
        userId: user?._id,
        username: user?.username,
        characterName: character?.name,
        timestamp: new Date()
      };

      setGame(prev => ({
        ...prev,
        messages: [...prev.messages, oocMessage]
      }));

      // Emit OOC message to other players in multiplayer
      if (isMultiplayer) {
        // TODO: Emit via socket in future
      }

      setSending(false);
      inputRef.current?.focus();
      return;
    }

    // Optimistically add player message
    setGame(prev => ({
      ...prev,
      messages: [...prev.messages, {
        role: 'player',
        content: action,
        timestamp: new Date()
      }]
    }));

    try {
      const response = await gameAPI.sendAction(gameId, action);

      // Add DM response
      setGame(prev => ({
        ...prev,
        messages: [...prev.messages, {
          role: 'dm',
          content: response.data.narrative,
          timestamp: new Date()
        }],
        currentLocation: response.data.gameState?.currentLocation || prev.currentLocation,
        worldState: response.data.gameState?.worldState || prev.worldState,
        inCombat: response.data.gameState?.inCombat || false,
        combatState: response.data.gameState?.combatState || prev.combatState
      }));

      // Update character stats
      if (response.data.character) {
        setCharacter(response.data.character);
      }

      // Update suggested actions
      if (response.data.suggestedActions) {
        setSuggestedActions(response.data.suggestedActions);
      } else {
        setSuggestedActions([]);
      }

      // Check if a roll is required
      if (response.data.rollRequired) {
        setPendingRoll(response.data.rollRequired);
        setShowDiceRoller(true);
      }
    } catch (err) {
      // Remove the optimistic message for any error
      setGame(prev => ({
        ...prev,
        messages: prev.messages.slice(0, -1)
      }));

      // Check if this was a content moderation block
      if (err.response?.status === 403 && err.response?.data?.blocked) {
        // Show severe warning toast for content policy violation
        addToast(
          'Your message was blocked due to a content policy violation. This type of content is not allowed.',
          'critical',
          15000
        );
      } else if (err.response?.status === 403) {
        // Handle turn-based errors specifically
        const errorMsg = err.response?.data?.error || 'Action not allowed';
        addToast(errorMsg, 'error', 5000);
      } else if (err.response?.status === 429) {
        // Rate limit or quota error
        const errorMsg = err.response?.data?.error || 'Too many requests, please slow down';
        addToast(errorMsg, 'warning', 5000);
      } else {
        setError('Failed to process action');
      }
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleSlashCommand = async (command) => {
    try {
      const response = await adminAPI.sendCommand(command, gameId);

      // Format the response based on command type
      let messageContent = '';

      if (response.data.success) {
        if (response.data.message) {
          messageContent = `[ADMIN] ${response.data.message}`;
        } else if (response.data.commands) {
          // /help command
          messageContent = `[ADMIN COMMANDS]\n${response.data.commands.map(c =>
            `${c.command} - ${c.description}`
          ).join('\n')}`;
        } else if (response.data.data) {
          // /spill command - format the data
          messageContent = `[ADMIN] ${response.data.target.toUpperCase()} INFO:\n${JSON.stringify(response.data.data, null, 2)}`;
        } else if (response.data.admins) {
          // /listadmins command
          messageContent = `[ADMIN LIST]\n${response.data.admins.map(a =>
            `${a.email} (${a.username})`
          ).join('\n')}`;
        } else {
          messageContent = '[ADMIN] Command executed successfully.';
        }

        // Update character if returned
        if (response.data.character) {
          setCharacter(response.data.character);
        }

        // Update game state if returned
        if (response.data.gameState) {
          setGame(prev => ({
            ...prev,
            worldState: {
              ...prev.worldState,
              timeOfDay: response.data.gameState.timeOfDay || prev.worldState.timeOfDay,
              dayNumber: response.data.gameState.dayNumber || prev.worldState.dayNumber
            }
          }));
        }
      }

      // Add system message with result
      setGame(prev => ({
        ...prev,
        messages: [...prev.messages, {
          role: 'system',
          content: messageContent,
          timestamp: new Date()
        }]
      }));

    } catch (err) {
      const errorMsg = err.response?.data?.message ||
        err.response?.data?.error?.message ||
        'Command failed';

      setGame(prev => ({
        ...prev,
        messages: [...prev.messages, {
          role: 'system',
          content: `[ERROR] ${errorMsg}`,
          timestamp: new Date()
        }]
      }));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleRollComplete = async (rollResult) => {
    setShowDiceRoller(false);

    // Add roll message
    setGame(prev => ({
      ...prev,
      messages: [...prev.messages, {
        role: 'system',
        content: `Rolled ${rollResult.dieType}: ${rollResult.roll}${rollResult.modifier ? ` + ${rollResult.modifier}` : ''} = ${rollResult.total}${pendingRoll?.dc ? ` vs DC ${pendingRoll.dc} (${rollResult.total >= pendingRoll.dc ? 'Success!' : 'Failure'})` : ''}`,
        timestamp: new Date()
      }]
    }));

    // Submit roll result to continue the action
    setSending(true);
    try {
      const response = await gameAPI.sendAction(gameId, `[Roll result: ${rollResult.total}]`, {
        ...rollResult,
        dc: pendingRoll?.dc
      });

      setGame(prev => ({
        ...prev,
        messages: [...prev.messages, {
          role: 'dm',
          content: response.data.narrative,
          timestamp: new Date()
        }]
      }));

      if (response.data.character) {
        setCharacter(response.data.character);
      }
    } catch (err) {
      setError('Failed to process roll result');
    } finally {
      setSending(false);
      setPendingRoll(null);
    }
  };

  const handleQuickAction = (actionText) => {
    setInput(actionText);
    // Clear suggested actions when one is selected
    setSuggestedActions([]);
    // Auto-focus and optionally auto-submit
    inputRef.current?.focus();
  };

  // Handle input change with typing indicator and character limit
  const handleInputChange = (e) => {
    const value = e.target.value;
    // Enforce character limit
    if (value.length <= MESSAGE_CHAR_LIMIT) {
      setInput(value);
    }
    if (isMultiplayer) {
      handleTyping();
    }
  };

  // Kick a player from the session (host only)
  const handleKickPlayer = async (userId) => {
    if (!confirm('Are you sure you want to remove this player?')) return;

    try {
      await sessionAPI.kickPlayer(gameId, userId);
      setParticipants(prev =>
        prev.filter(p => (p.user?._id || p.user) !== userId)
      );
      addToast('Player removed from session.', 'success', 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to kick player');
    }
  };

  // Ban a player from the session (host only)
  const handleBanPlayer = async (userId) => {
    if (!confirm('Are you sure you want to ban this player? They will not be able to rejoin this session.')) return;

    try {
      await sessionAPI.banPlayer(gameId, userId);
      setParticipants(prev =>
        prev.map(p =>
          (p.user?._id || p.user) === userId
            ? { ...p, isActive: false, isBanned: true }
            : p
        )
      );
      addToast('Player banned from session.', 'success', 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to ban player');
    }
  };

  // View a player's profile
  const handleViewProfile = (userId) => {
    if (userId) {
      setViewingProfile(userId);
    }
  };

  // Update game settings callback
  const handleGameUpdate = (updates) => {
    setGame(prev => prev ? { ...prev, ...updates } : prev);
  };

  const handleConsume = async (itemName) => {
    try {
      const response = await gameAPI.consumeItem(gameId, itemName);

      setGame(prev => ({
        ...prev,
        messages: [...prev.messages, {
          role: 'narrator',
          content: response.data.effect,
          timestamp: new Date()
        }]
      }));

      if (response.data.character) {
        setCharacter(response.data.character);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to consume item');
    }
  };

  const handleDropItem = async (itemId) => {
    try {
      const response = await gameAPI.dropItem(gameId, itemId);

      setGame(prev => ({
        ...prev,
        messages: [...prev.messages, {
          role: 'narrator',
          content: response.data.message,
          timestamp: new Date()
        }]
      }));

      if (response.data.character) {
        setCharacter(response.data.character);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to drop item');
    }
  };

  const formatMessage = (msg) => {
    return msg.content.split('\n').map((line, i) => (
      <p key={i}>{line}</p>
    ));
  };

  if (loading) {
    return (
      <div className="game-loading">
        <div className="spinner"></div>
        <p>Entering the realm...</p>
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className="game-error-page">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/play')} className="btn btn-primary">
          Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="game-container">
      {/* Header */}
      <header className="game-header">
        <div className="header-left">
          <button onClick={() => navigate('/play')} className="btn btn-secondary btn-sm">
            Exit
          </button>
          <div className="currency-display">
            <span className="currency gold" title="Gold">
              <span className="currency-icon">●</span>
              {character?.currency?.gold || 0}
            </span>
            <span className="currency silver" title="Silver">
              <span className="currency-icon">●</span>
              {character?.currency?.silver || 0}
            </span>
            <span className="currency copper" title="Copper">
              <span className="currency-icon">●</span>
              {character?.currency?.copper || 0}
            </span>
          </div>
        </div>
        <div className="header-info">
          <span className="location-name">{game?.currentLocation?.name || 'Unknown'}</span>
          <span className="time-display">{game?.worldState?.timeOfDay}</span>
          {/* Turn Timer for Multiplayer */}
          {isMultiplayer && turnState?.turnEndTime && (
            <TurnTimer
              turnEndTime={turnState.turnEndTime}
              isYourTurn={isYourTurn}
              currentPlayerName={currentTurnPlayer?.characterName || currentTurnPlayer?.username}
              onTimeWarning={handleTimeWarning}
              onTimeExpired={handleTimeExpired}
            />
          )}
        </div>
        <div className="header-actions">
          <button
            onClick={() => setShowInventory(true)}
            className="btn btn-secondary btn-sm"
          >
            Inventory
          </button>
          <button
            onClick={() => setShowCharacter(!showCharacter)}
            className={`btn btn-secondary btn-sm ${showCharacter ? 'active' : ''}`}
          >
            Character
          </button>
          <button
            onClick={() => setShowJournal(!showJournal)}
            className={`btn btn-secondary btn-sm ${showJournal ? 'active' : ''}`}
          >
            Journal
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="settings-btn"
            title="Settings"
          >
            ⚙️
          </button>
          {/* Mobile hamburger menu button */}
          <button
            onClick={() => setShowMobileMenu(true)}
            className="mobile-menu-btn"
            aria-label="Menu"
          >
            ☰
          </button>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      <div
        className={`mobile-nav-overlay ${showMobileMenu ? 'open' : ''}`}
        onClick={() => setShowMobileMenu(false)}
      >
        <div className="mobile-nav-panel" onClick={(e) => e.stopPropagation()}>
          <div className="mobile-nav-header">
            <h3>Menu</h3>
            <button
              className="close-btn"
              onClick={() => setShowMobileMenu(false)}
            >
              ×
            </button>
          </div>
          <div className="mobile-nav-content">
            <button
              className="mobile-nav-item"
              onClick={() => {
                setShowInventory(true);
                setShowMobileMenu(false);
              }}
            >
              <span className="nav-icon">🎒</span>
              Inventory
            </button>
            <button
              className="mobile-nav-item"
              onClick={() => {
                setShowCharacter(!showCharacter);
                setShowMobileMenu(false);
              }}
            >
              <span className="nav-icon">👤</span>
              Character
            </button>
            <button
              className="mobile-nav-item"
              onClick={() => {
                setShowJournal(!showJournal);
                setShowMobileMenu(false);
              }}
            >
              <span className="nav-icon">📖</span>
              Journal
            </button>
            <button
              className="mobile-nav-item"
              onClick={() => {
                setShowSettings(true);
                setShowMobileMenu(false);
              }}
            >
              <span className="nav-icon">⚙️</span>
              Settings
            </button>
            <div className="mobile-nav-divider" />
            <button
              className="mobile-nav-item danger"
              onClick={() => {
                setShowMobileMenu(false);
                navigate('/play');
              }}
            >
              <span className="nav-icon">🚪</span>
              Exit Game
            </button>
          </div>
        </div>
      </div>

      {/* Combat Banner */}
      {game?.inCombat && (
        <div className="combat-banner">
          COMBAT - Round {game.combatState?.round || 1}
        </div>
      )}

      {/* Main Content */}
      <div className="game-main">
        {/* Messages */}
        <div className="messages-container">
          <div className="messages-scroll">
            {game?.messages?.map((msg, idx) => (
              <div key={idx} className={`message message-${msg.role}`}>
                {msg.role === 'dm' && <span className="message-label">Dungeon Master</span>}
                {msg.role === 'player' && <span className="message-label">You</span>}
                {msg.role === 'narrator' && <span className="message-label">Narrator</span>}
                {msg.role === 'ooc' && (
                  <span className="message-label ooc-label">
                    [OOC]{' '}
                    <span
                      className={`clickable-username ${msg.userId ? '' : 'no-link'}`}
                      onClick={() => msg.userId && handleViewProfile(msg.userId)}
                    >
                      {msg.characterName || msg.username}
                    </span>
                  </span>
                )}
                <div className="message-content">
                  {formatMessage(msg)}
                </div>
              </div>
            ))}
            {sending && (
              <div className="message message-loading">
                <div className="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Side Panels */}
        {showCharacter && (
          <CharacterPanel
            character={character}
            onClose={() => setShowCharacter(false)}
            onConsume={handleConsume}
          />
        )}

        {showJournal && (
          <Journal
            gameId={gameId}
            onClose={() => setShowJournal(false)}
          />
        )}
      </div>

      {/* Inventory Modal */}
      {showInventory && (
        <Inventory
          character={character}
          onClose={() => setShowInventory(false)}
          onDropItem={handleDropItem}
          onUseItem={handleConsume}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <GameSettings
          onClose={() => setShowSettings(false)}
          game={game}
          isHost={isHost}
          onGameUpdate={handleGameUpdate}
        />
      )}

      {/* Player List (Multiplayer) */}
      {isMultiplayer && (
        <PlayerList
          gameId={gameId}
          participants={participants}
          hostId={game?.user || game?.hostUser}
          turnState={turnState}
          isHost={isHost}
          onKickPlayer={handleKickPlayer}
          onBanPlayer={handleBanPlayer}
          onViewProfile={handleViewProfile}
        />
      )}

      {/* Vital Stats Bar */}
      <div className="vital-stats-bar">
        <div className="stat-item">
          <span className="stat-label">HP</span>
          <div className={`stat-bar health ${character?.vitalStats?.health?.current / character?.vitalStats?.health?.max < 0.3 ? 'low' : character?.vitalStats?.health?.current / character?.vitalStats?.health?.max < 0.6 ? 'mid' : ''}`}>
            <div
              className="stat-bar-fill"
              style={{ width: `${(character?.vitalStats?.health?.current / character?.vitalStats?.health?.max) * 100}%` }}
            />
            <span className="stat-bar-label">
              {character?.vitalStats?.health?.current}/{character?.vitalStats?.health?.max}
            </span>
          </div>
        </div>
        <div className="stat-item">
          <span className="stat-label">STA</span>
          <div className="stat-bar stamina">
            <div
              className="stat-bar-fill"
              style={{ width: `${(character?.vitalStats?.stamina?.current / character?.vitalStats?.stamina?.max) * 100}%` }}
            />
            <span className="stat-bar-label">
              {character?.vitalStats?.stamina?.current}/{character?.vitalStats?.stamina?.max}
            </span>
          </div>
        </div>
        <div className="stat-item">
          <span className="stat-label">HUN</span>
          <div className="stat-bar hunger">
            <div
              className="stat-bar-fill"
              style={{ width: `${character?.vitalStats?.hunger?.current}%` }}
            />
            <span className="stat-bar-label">{character?.vitalStats?.hunger?.current}%</span>
          </div>
        </div>
        <div className="stat-item">
          <span className="stat-label">THI</span>
          <div className="stat-bar thirst">
            <div
              className="stat-bar-fill"
              style={{ width: `${character?.vitalStats?.thirst?.current}%` }}
            />
            <span className="stat-bar-label">{character?.vitalStats?.thirst?.current}%</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {suggestedActions.length > 0 && !sending && !showDiceRoller && (
        <div className="quick-actions">
          {suggestedActions.map((action, idx) => (
            <button
              key={idx}
              className="quick-action-btn"
              onClick={() => handleQuickAction(action.text)}
              title={action.detail || action.text}
            >
              <span className="quick-action-text">{action.text}</span>
              {action.detail && (
                <span className="quick-action-detail">{action.detail}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="game-input-container">
        {/* Character Counter & OOC Toggle */}
        <div className="input-controls">
          <button
            type="button"
            className={`ooc-toggle ${isOOC ? 'active' : ''}`}
            onClick={() => setIsOOC(!isOOC)}
            title={isOOC ? "OOC mode: Messages won't trigger AI" : "Click for out-of-character chat"}
          >
            OOC
          </button>
          <span className={`char-counter ${charsRemaining < 50 ? 'warning' : ''} ${charsRemaining < 20 ? 'critical' : ''}`}>
            {charsRemaining}
          </span>
        </div>

        <form className="game-input" onSubmit={handleSendAction}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={handleInputChange}
            placeholder={
              isOOC
                ? "Out-of-character message..."
                : isMultiplayer && !isYourTurn
                  ? "Wait for your turn..."
                  : "What do you do?"
            }
            disabled={sending || showDiceRoller || (isMultiplayer && !isYourTurn && !isOOC)}
            autoFocus
            maxLength={MESSAGE_CHAR_LIMIT}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={sending || !input.trim() || showDiceRoller || (isMultiplayer && !canSendMessage && !isOOC)}
          >
            {sending ? '...' : isOOC ? 'Chat' : 'Act'}
          </button>
          {/* End Turn Button (Multiplayer) */}
          {isMultiplayer && isYourTurn && !isOOC && (
            <button
              type="button"
              className="btn btn-secondary end-turn-btn"
              onClick={handleEndTurn}
              title="End your turn early"
            >
              End Turn
            </button>
          )}
          {/* Host Take Control Button (when locked out) */}
          {isMultiplayer && isHost && !isYourTurn && (
            <button
              type="button"
              className="btn btn-warning host-control-btn"
              onClick={handleForceHostTurn}
              title="Take control as host (use if stuck)"
            >
              Take Control
            </button>
          )}
        </form>
      </div>

      {/* Dice Roller Modal */}
      {showDiceRoller && pendingRoll && (
        <DiceRoller
          dieType={pendingRoll.type}
          purpose={pendingRoll.purpose}
          modifier={pendingRoll.modifier || 0}
          dc={pendingRoll.dc}
          onRoll={handleRollComplete}
          onClose={() => {
            setShowDiceRoller(false);
            setPendingRoll(null);
          }}
        />
      )}

      {/* Error Toast */}
      {error && (
        <div className="error-toast" onClick={() => setError('')}>
          {error}
        </div>
      )}

      {/* Profile Popup */}
      {viewingProfile && (
        <ProfilePopup
          userId={viewingProfile}
          onClose={() => setViewingProfile(null)}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
